import React, { createContext, useContext, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

type Mode = 'light' | 'dark';

interface ThemeContextValue {
  mode: Mode;
  toggleMode: () => void;
}

const ThemeToggleContext = createContext<ThemeContextValue | undefined>(undefined);

export function useThemeToggle() {
  const ctx = useContext(ThemeToggleContext);
  if (!ctx) throw new Error('useThemeToggle must be used within ThemeProvider');
  return ctx;
}

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>('dark');
  const toggleMode = () => setMode((m) => (m === 'dark' ? 'light' : 'dark'));

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === 'dark'
            ? {
                background: { default: '#121212', paper: '#1e1e1e' }
              }
            : { background: { default: '#f5f5f5', paper: '#ffffff' } })
        }
      }),
    [mode]
  );

  return (
    <ThemeToggleContext.Provider value={{ mode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeToggleContext.Provider>
  );
}
